import * as R from 'ramda';
import { LocationState } from '../../../../types/Base.types';
import { OrderBy } from '../../../Hooks/useList.types';
import {
  BaseItem,
  Column,
  FirstColumnIndentParams,
  ParentCheckboxState,
  TreeCellOffsetParams,
} from './VirtualizedTable.types';
import { setLastVisited } from './lastVisitedFromList';
import { NESTED_CHILDREN_SPACING } from './tree/tree.consts';
import { TreeIndentOrigin } from './tree/tree.types';
import {
  CHECKBOX_COLUMN_PADDING,
  COLUMN_GAP,
  FIRST_COLUMN_PADDING_WITHOUT_CHECKBOX,
  FIRST_COLUMN_PADDING_WITH_CHECKBOX,
} from './virtualizedTable.styles';

export const isParent = <T extends BaseItem>(row: T, nextRow: T): boolean =>
  nextRow && nextRow.parentId === row.id && !R.isNil(row.id);

export const getDefaultOrderFromColumns = <T>(columns: Column<T>[]): OrderBy => {
  const defaultColumn = columns.find((column) => column.defaultSort);

  if (!defaultColumn?.defaultSort) {
    return {};
  }

  return { [defaultColumn.orderByName ?? defaultColumn.name]: defaultColumn.defaultSort };
};

export const getParentCheckboxState = (
  selectedCount: number,
  totalCount: number,
): ParentCheckboxState => {
  if (totalCount === 0 || selectedCount === 0) {
    return 'unchecked';
  }

  if (selectedCount === totalCount) {
    return 'checked';
  }

  return 'indeterminate';
};

export const buildLinkTo = (href: string, hrefState: LocationState | undefined) =>
  hrefState ? { pathname: href, state: hrefState } : href;

export const trackVisitedListItem = (
  hrefState: LocationState | undefined,
  itemId: string,
): void => {
  if (hrefState?.originUrl) {
    setLastVisited(hrefState.originUrl, itemId);
  }
};

export const getFirstColumnPaddingLeft = ({
  row,
  hasRowCheckbox,
}: FirstColumnIndentParams): number => {
  if (row.isChild && !hasRowCheckbox) {
    return FIRST_COLUMN_PADDING_WITHOUT_CHECKBOX + NESTED_CHILDREN_SPACING * row.depth;
  }

  if (!row.parentId) {
    return hasRowCheckbox
      ? FIRST_COLUMN_PADDING_WITH_CHECKBOX
      : FIRST_COLUMN_PADDING_WITHOUT_CHECKBOX;
  }

  return COLUMN_GAP;
};

export const getFirstColumnIndentShift = ({
  row,
  hasRowCheckbox,
}: FirstColumnIndentParams): number =>
  row.isChild && hasRowCheckbox ? NESTED_CHILDREN_SPACING * row.depth : 0;

export const getTreeCellOffset = ({
  depth,
  hasRowCheckbox,
}: TreeCellOffsetParams): TreeIndentOrigin => ({
  cellShiftInLevels: hasRowCheckbox ? depth : 0,
  leadingSpacing: hasRowCheckbox ? CHECKBOX_COLUMN_PADDING : FIRST_COLUMN_PADDING_WITHOUT_CHECKBOX,
});
